import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../integrations/supabase/client';

const { FiStar, FiMapPin, FiHeart, FiCoffee } = FiIcons;

const Testimonials = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [hoveredTestimonial, setHoveredTestimonial] = useState(null);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const fetchTestimonials = async () => {
      const { data } = await supabase.from('testimonials').select('*').order('created_at', { ascending: false });
      if (data) setTestimonials(data);
    };
    fetchTestimonials();
  }, []);

  const stats = [
    { number: "50K+", label: "Happy Customers", labelAr: "عميل سعيد" },
    { number: "4.9/5", label: "Average Rating", labelAr: "متوسط التقييم" },
    { number: "95%", label: "Customer Retention", labelAr: "الاحتفاظ بالعملاء" },
    { number: "27", label: "Egyptian Cities", labelAr: "مدينة مصرية" }
  ];

  return (
    <section id="testimonials" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-green-800 mb-6">
            {t('testimonials.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('testimonials.subtitle')}
          </p>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05 }}
              className="text-center bg-gradient-to-br from-green-50 to-amber-50 p-6 rounded-2xl shadow-lg"
            >
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-3xl md:text-4xl font-bold text-green-800 mb-2"
              >
                {stat.number}
              </motion.div>
              <p className="text-gray-600 font-medium">{stat.label}</p>
              <p className="text-green-600 text-sm">{stat.labelAr}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Dynamic Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              onMouseEnter={() => setHoveredTestimonial(testimonial.id)}
              onMouseLeave={() => setHoveredTestimonial(null)}
              className="bg-gradient-to-br from-green-50 to-amber-50 rounded-2xl p-6 shadow-lg relative overflow-hidden"
            >
              {hoveredTestimonial === testimonial.id && (
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-br from-green-200 to-amber-200 opacity-20"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.3 }}
                />
              )}
              
              <div className="relative z-10">
                <div className="flex items-center mb-4">
                  <motion.img
                    src={testimonial.avatar_url || "https://placehold.co/100"}
                    alt={testimonial.name_en}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                    whileHover={{ scale: 1.1 }}
                  />
                  <div className="flex-1">
                    <h4 className="font-bold text-green-800">
                      {isRTL ? (testimonial.name_ar || testimonial.name_en) : testimonial.name_en}
                    </h4>
                    <div className="flex items-center mt-1">
                      <SafeIcon icon={FiMapPin} className="w-3 h-3 text-gray-500 mr-1" />
                      <span className="text-xs text-gray-500">
                         {isRTL ? (testimonial.location_ar || testimonial.location_en) : testimonial.location_en}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <motion.div key={i} whileHover={{ scale: 1.2, rotate: 10 }}>
                      <SafeIcon icon={FiStar} className="w-4 h-4 text-yellow-400 fill-current" />
                    </motion.div>
                  ))}
                </div>

                <blockquote className="text-gray-700 leading-relaxed mb-4">
                  "{isRTL ? (testimonial.text_ar || testimonial.text_en) : testimonial.text_en}"
                </blockquote>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;